import React, { useEffect, useState, useRef } from 'react';
import { FaTrash, FaPlus, FaSearch, FaCalendar, FaMapMarkerAlt, FaInfoCircle, FaCrop, FaUndo, FaCheck, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import { FaPencil } from "react-icons/fa6";
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import "../../assets/CSS/app.css";
import ApiService from '../../models/ApiService';
import { toast, Toaster } from 'react-hot-toast';

const Shayars = () => {
    const [shayars, setShayars] = useState([]);
    const [formData, setFormData] = useState({
        id: null,
        shayar_name: '',
        shayar_urdu: "",
        shayar_hindi: "",
        year: "",
        place: "",
        brief_description: "",
        description: "",
        image_url: "",
        is_published: "",
    });
    const [imageFile, setImageFile] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [isImageEditorOpen, setIsImageEditorOpen] = useState(false);
    const [selectedShayar, setSelectedShayar] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'active', 'inactive'

    // React Image Crop states
    const [crop, setCrop] = useState();
    const [completedCrop, setCompletedCrop] = useState();
    const [originalImage, setOriginalImage] = useState(null);
    const imgRef = useRef(null);
    const previewCanvasRef = useRef(null);

    const [toggleLoading, setToggleLoading] = useState(null);

    // Circle aspect ratio for profile image
    const CIRCLE_ASPECT = 1;

    useEffect(() => {
        fetchShayar();
    }, []);

    // Initialize crop when image loads
    useEffect(() => {
        if (isImageEditorOpen && originalImage && imgRef.current) {
            const { width, height } = imgRef.current;
            const crop = centerCrop(
                makeAspectCrop(
                    {
                        unit: '%',
                        width: 90,
                    },
                    CIRCLE_ASPECT,
                    width,
                    height
                ),
                width,
                height
            );
            setCrop(crop);
        }
    }, [isImageEditorOpen, originalImage]);

    // Update canvas when crop changes
    useEffect(() => {
        if (completedCrop?.width && completedCrop?.height && imgRef.current && previewCanvasRef.current) {
            canvasPreview(
                imgRef.current,
                previewCanvasRef.current,
                completedCrop
            );
        }
    }, [completedCrop]);

    const fetchShayar = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await ApiService.Shayar();
            console.log(res);
            setShayars(res.data.responseContent || []);
        } catch (error) {
            console.error('Error fetching shayars:', error);
            setError(error.response?.data?.message || 'Error fetching shayars');
        } finally {
            setIsLoading(false);
        }
    };

    // Filter shayars based on search term and active filter
    const filteredShayars = shayars.filter(shayar => {
        const matchesSearch =
            shayar.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            shayar.urdu?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            shayar.hindi?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            shayar.place?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            shayar.year?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            shayar.id;

        return matchesSearch;
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Handle toggle status change
    const handleToggleStatus = async (shayarId, currentStatus) => {
        setToggleLoading(shayarId);

        try {
            const res = await ApiService.ToggleShayar(shayarId);

            // Toggle locally after success
            setShayars(prev =>
                prev.map(shayar =>
                    shayar.id === shayarId || shayar.id === shayarId
                        ? { ...shayar, is_published: !currentStatus }
                        : shayar
                )
            );

        } catch (error) {
            console.error("Error updating shayar status:", error);
            toast.error("Failed to update status");
        } finally {
            setToggleLoading(null);
        }
    };


    // Canvas preview function
    async function canvasPreview(image, canvas, crop) {
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;

        const pixelRatio = window.devicePixelRatio;
        canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
        canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

        ctx.scale(pixelRatio, pixelRatio);
        ctx.imageSmoothingQuality = 'high';

        const cropX = crop.x * scaleX;
        const cropY = crop.y * scaleY;

        ctx.save();

        // Create circular clipping path
        const centerX = canvas.width / pixelRatio / 2;
        const centerY = canvas.height / pixelRatio / 2;
        const radius = Math.min(centerX, centerY);

        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
        ctx.closePath();
        ctx.clip();

        ctx.drawImage(
            image,
            cropX,
            cropY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            canvas.width / pixelRatio,
            canvas.height / pixelRatio
        );

        ctx.restore();
    }

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                toast.error('Please select a valid image file');
                return;
            }

            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                toast.error('Image size should be less than 5MB');
                return;
            }

            setImageFile(file);

            // Open image editor
            const reader = new FileReader();
            reader.onload = (e) => {
                setOriginalImage(e.target.result);
                setIsImageEditorOpen(true);
            };
            reader.readAsDataURL(file);
        }
    };

    const onImageLoad = (e) => {
        const { width, height } = e.currentTarget;
        const crop = centerCrop(
            makeAspectCrop(
                {
                    unit: '%',
                    width: 90,
                },
                CIRCLE_ASPECT,
                width,
                height
            ),
            width,
            height
        );
        setCrop(crop);
    };

    const resetCrop = () => {
        if (imgRef.current) {
            const { width, height } = imgRef.current;
            const crop = centerCrop(
                makeAspectCrop(
                    {
                        unit: '%',
                        width: 90,
                    },
                    CIRCLE_ASPECT,
                    width,
                    height
                ),
                width,
                height
            );
            setCrop(crop);
        }
    };

    const applyCrop = async () => {
        if (!previewCanvasRef.current) {
            toast.error('No image to crop');
            return;
        }

        try {
            previewCanvasRef.current.toBlob((blob) => {
                if (!blob) {
                    toast.error('Failed to process image');
                    return;
                }

                const editedFile = new File([blob], imageFile.name, {
                    type: 'image/png',
                    lastModified: new Date().getTime()
                });
                setImageFile(editedFile);

                // Update preview
                const imageUrl = URL.createObjectURL(blob);
                setFormData(prev => ({ ...prev, image_url: imageUrl }));

                setIsImageEditorOpen(false);
                setCrop(undefined);
                setCompletedCrop(undefined);
                toast.success('Image cropped successfully!');
            }, 'image/png');
        } catch (error) {
            console.error('Error applying crop:', error);
            toast.error('Failed to crop image');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            // Use FormData for file + other fields
            const formDataToSend = new FormData();
            formDataToSend.append("shayar_name", formData.shayar_name);
            formDataToSend.append("shayar_urdu", formData.shayar_urdu);
            formDataToSend.append("shayar_hindi", formData.shayar_hindi);
            formDataToSend.append("year", formData.year);
            formDataToSend.append("place", formData.place);
            formDataToSend.append("brief_description", formData.brief_description);
            formDataToSend.append("description", formData.description);
            formDataToSend.append("is_published", formData.is_published);

            if (imageFile) {
                formDataToSend.append("file", imageFile);
            }

            if (formData.id) {
                await ApiService.UpdateShayar(formData.id, formDataToSend);
                toast.success("Updated Successfully");
            } else {
                await ApiService.AddShayar(formDataToSend);
                toast.success("Shayar Added Successfully");
            }

            await fetchShayar();
            setIsModalOpen(false);
            resetForm();
        } catch (error) {
            console.error("Error saving shayar:", error);
            setError("Failed to save shayar. Please try again.");
            toast.error("Failed to save shayar");
        } finally {
            setIsLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            id: null,
            shayar_name: '',
            shayar_urdu: "",
            shayar_hindi: "",
            year: "",
            place: "",
            brief_description: "",
            description: "",
            image_url: "",
            is_published: ''
        });
        setImageFile(null);
        setError(null);
    };

    const handleEdit = (shayar) => {
        setFormData({
            id: shayar.id || shayar._id,
            shayar_name: shayar.name || shayar.shayar_name,
            shayar_urdu: shayar.urdu || shayar.shayar_urdu,
            shayar_hindi: shayar.hindi || shayar.shayar_hindi,
            year: shayar.year || "",
            place: shayar.place || "",
            brief_description: shayar.brief_description || "",
            description: shayar.description || "",
            image_url: shayar.image_url || "",
            is_published: shayar.is_published !== false // Default to true if not specified
        });
        setImageFile(null);
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this shayar?')) {
            setIsLoading(true);
            setError(null);
            try {
                await ApiService.DeleteShayar(id);
                await fetchShayar();
                toast.error("Shaa'ir Deleted Successfully");
            } catch (error) {
                console.error('Error deleting shayar:', error);
                setError('Failed to delete shayar. Please try again.');
                toast.error("Failed to delete shayar");
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleViewDetails = (shayar) => {
        setSelectedShayar(shayar);
        setIsDetailModalOpen(true);
    };

    const getDefaultImage = (name) => {
        return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random`;
    };

    // Count active/inactive shayars for statistics
    const activeShayarsCount = shayars.filter(s => s.is_published !== false).length;
    const inactiveShayarsCount = shayars.filter(s => s.is_published === false).length;

    return (
        <div className="mx-auto" id='primary-fonts'>
            <Toaster />
            <h1 className="text-2xl font-bold mb-8 text-[--secondary-color]">Shaa'ir List</h1>

            {/* Error message */}
            {error && (
                <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                    {error}
                </div>
            )}



            {/* Search and Add Button */}
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                <div className="relative w-full md:w-64">
                    <input
                        type="text"
                        placeholder="Search shaa'ir..."
                        className="w-full pl-10 pr-4 py-2 border border-[--border-color-o] focus:outline-none focus:ring-1 focus:ring-[--border-color-o] rounded-lg"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <FaSearch className="absolute left-3 top-3 text-gray-400" />
                </div>
                <button
                    onClick={() => setIsModalOpen(true)}
                    className="w-full md:w-auto bg-[--primary-btn-color] hover:bg-[--secondary-btn-color] text-white px-4 py-2 rounded-lg flex items-center gap-2 transition duration-200"
                    disabled={isLoading}
                >
                    <FaPlus /> Add Shaa'ir
                </button>
            </div>

            {/* Loading state */}
            {isLoading && !shayars.length ? (
                <div className="flex justify-center items-center h-40">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[--primary-color]"></div>
                </div>
            ) : (
                /* Card Grid */
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {filteredShayars.length > 0 ? (
                        filteredShayars.map((shayar, index) => (
                            <div
                                key={shayar.id || shayar._id}
                                className={`bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 border ${shayar.is_published === false ? 'opacity-60' : ''
                                    }`}
                            >


                                {/* Card Header with Image */}
                                <div className="relative h-auto bg-[--bg-color] p-4">
                                    <div className="w-32 h-32 mx-auto rounded-full">
                                        <img
                                            src={shayar.image_url || getDefaultImage(shayar.name || shayar.shayar_name)}
                                            alt={shayar.name || shayar.shayar_name}
                                            className="w-full h-full rounded-full object-cover"
                                            onError={(e) => {
                                                e.target.src = getDefaultImage(shayar.name || shayar.shayar_name);
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Card Content */}
                                <div className="p-5">
                                    {/* Name Section */}
                                    <div className="text-center mb-4">
                                        <h3 className="text-lg font-bold text-[--primary-color] mb-1">
                                            {shayar.name || shayar.shayar_name}
                                        </h3>
                                        <div className="p-3 text-[--text-color-g] " dir="rtl" id='primary-urdu-fonts'>
                                            {shayar.urdu || shayar.shayar_urdu}
                                        </div>
                                        <div className="text-[--text-color-g] mt-1">
                                            {shayar.hindi || shayar.shayar_hindi}
                                        </div>
                                    </div>



                                    {/* Action Buttons - Split Layout */}
                                    <div className="flex justify-between items-center pt-3 border-t border-[--border-color-g]">
                                        {/* Details Button - Left Side */}
                                        <button
                                            onClick={() => handleViewDetails(shayar)}
                                            className="flex items-center gap-2 px-3 py-2 text-[--primary-btn-color] hover:text-[--secondary-btn-color] hover:bg-blue-50 rounded-lg transition-colors"
                                        >
                                            <FaInfoCircle className="text-sm" />
                                            {/* <span className="text-xs">Details</span> */}
                                        </button>

                                        {/* Toggle, Edit, Delete - Right Side */}
                                        <div className="flex items-center gap-2">
                                            {/* Toggle Switch */}
                                            <button
                                                onClick={() => handleToggleStatus(shayar.id, shayar.is_published)}
                                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 ease-in-out focus:outline-none 
    ${shayar.is_published ? 'bg-green-500' : 'bg-gray-300'}
    ${toggleLoading === shayar.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-90'}`}
                                                disabled={toggleLoading === shayar.id}
                                            >
                                                <span
                                                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-all duration-300 ease-in-out 
        ${shayar.is_published ? 'translate-x-6' : 'translate-x-1'}
        ${toggleLoading === shayar.id ? 'opacity-70' : ''}`}
                                                />

                                                {toggleLoading === shayar.id && (
                                                    <div className="absolute inset-0 flex items-center justify-center">
                                                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                                    </div>
                                                )}

                                                <span className="sr-only">
                                                    {shayar.is_published ? 'Published' : 'Unpublished'}
                                                </span>
                                            </button>


                                            {/* Edit Button */}
                                            <button
                                                onClick={() => handleEdit(shayar)}
                                                className="flex items-center justify-center w-8 h-8 text-[--primary-btn-color] hover:bg-[--primary-btn-color] hover:text-white rounded-full transition-colors"
                                                title="Edit"
                                            >
                                                <FaPencil className="text-sm" />
                                            </button>

                                            {/* Delete Button */}
                                            <button
                                                onClick={() => handleDelete(shayar.id || shayar._id)}
                                                className="flex items-center justify-center w-8 h-8 text-red-600 hover:bg-red-500 hover:text-white rounded-full transition-colors"
                                                title="Delete"
                                            >
                                                <FaTrash className="text-sm" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="col-span-full text-center py-12">
                            <div className="text-gray-400 text-lg">No shayars found</div>
                            <button
                                onClick={() => setIsModalOpen(true)}
                                className="mt-4 bg-[--primary-btn-color] hover:bg-[--secondary-btn-color] text-white px-6 py-2 rounded-lg flex items-center gap-2 transition duration-200 mx-auto"
                            >
                                <FaPlus /> Add First Shaa'ir
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Add/Edit Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex justify-between items-start mb-4">
                                <h2 className="text-xl font-semibold mb-4">
                                    {formData.id ? 'Edit Shaa\'ir' : 'Add New Shaa\'ir'}
                                </h2>
                                <button
                                    onClick={() => {
                                        setIsModalOpen(false);
                                        resetForm();
                                    }}
                                    className="text-black hover:text-black text-2xl"
                                >
                                    ×
                                </button>
                            </div>
                            {error && (
                                <div className="mb-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                                    {error}
                                </div>
                            )}
                            <form onSubmit={handleSubmit}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Image Upload Section */}
                                    <div className="mb-4 md:col-span-2">
                                        <label className="block text-[--text-color] mb-2" htmlFor="image_upload">
                                            Profile Image
                                        </label>
                                        <div className="flex items-center gap-4">
                                            <div className="w-20 h-20 rounded-full bg-gray-200 overflow-hidden border-2 border-[--border-color-o]">
                                                <img
                                                    src={formData.image_url || getDefaultImage(formData.shayar_name)}
                                                    alt="Preview"
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                            <div className="flex-1">
                                                <input
                                                    type="file"
                                                    id="image_upload"
                                                    accept="image/*"
                                                    onChange={handleFileChange}
                                                    className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                                                    disabled={isLoading}
                                                />
                                                <p className="text-xs text-gray-500 mt-1">Select an image file (JPG, PNG, WEBP - Max 5MB)</p>
                                                {imageFile && (
                                                    <p className="text-xs text-green-600 mt-1">
                                                        Image selected: {imageFile.name}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>


                                    {/* Rest of the form fields */}
                                    <div className="mb-4">
                                        <label className="block text-[--text-color] mb-2" htmlFor="shayar_name">
                                            Shayar Name (English) *
                                        </label>
                                        <input
                                            type="text"
                                            id="shayar_name"
                                            name="shayar_name"
                                            value={formData.shayar_name}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                                            required
                                            disabled={isLoading}
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-[--text-color] mb-2" htmlFor="shayar_urdu">
                                            Shayar Name Urdu *
                                        </label>
                                        <input
                                            type="text"
                                            name="shayar_urdu"
                                            dir='rtl'
                                            value={formData.shayar_urdu}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o] primary-urdu-fonts"
                                            required
                                            disabled={isLoading}
                                        />
                                    </div>

                                    {/* ... rest of your form fields remain the same ... */}
                                    <div className="mb-4">
                                        <label className="block text-[--text-color] mb-2" htmlFor="shayar_hindi">
                                            Shayar Name Hindi *
                                        </label>
                                        <input
                                            type="text"
                                            id="shayar_hindi"
                                            name="shayar_hindi"
                                            value={formData.shayar_hindi}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                                            required
                                            disabled={isLoading}
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-[--text-color] mb-2" htmlFor="year">
                                            Years (e.g., 1877–1938)
                                        </label>
                                        <input
                                            type="text"
                                            id="year"
                                            name="year"
                                            value={formData.year}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                                            disabled={isLoading}
                                        />
                                    </div>

                                    <div className="mb-4 md:col-span-2">
                                        <label className="block text-[--text-color] mb-2" htmlFor="place">
                                            Place of Origin
                                        </label>
                                        <input
                                            type="text"
                                            id="place"
                                            name="place"
                                            value={formData.place}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                                            disabled={isLoading}
                                        />
                                    </div>

                                    <div className="mb-4 md:col-span-2">
                                        <label className="block text-[--text-color] mb-2" htmlFor="brief_description">
                                            Brief Description
                                        </label>
                                        <textarea
                                            id="brief_description"
                                            name="brief_description"
                                            value={formData.brief_description}
                                            onChange={handleInputChange}
                                            rows="3"
                                            className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                                            disabled={isLoading}
                                            placeholder="Short biography or notable achievements..."
                                        />
                                    </div>

                                    <div className="mb-4 md:col-span-2">
                                        <label className="block text-[--text-color] mb-2" htmlFor="description">
                                            Description
                                        </label>
                                        <textarea
                                            id="description"
                                            name="description"
                                            value={formData.description}
                                            onChange={handleInputChange}
                                            rows="5"
                                            className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                                            disabled={isLoading}
                                            placeholder="Comprehensive biography and literary contributions..."
                                        />
                                    </div>
                                </div>

                                <div className="flex justify-end space-x-3 mt-4">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsModalOpen(false);
                                            resetForm();
                                        }}
                                        className="px-4 py-2 bg-gray-300 text-black rounded-lg hover:bg-gray-400 transition duration-200"
                                        disabled={isLoading}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-[--primary-btn-color] text-white rounded-lg hover:bg-[--secondary-btn-color] transition duration-200"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? 'Uploading...' : (formData.id ? 'Update' : 'Save')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Image Editor Modal - remains the same */}
            {isImageEditorOpen && originalImage && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-screen-sm max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl text-[--text-color-g] font-semibold flex items-center gap-2">
                                    <FaCrop /> Crop Image for Circle
                                </h2>
                                <button
                                    onClick={() => {
                                        setIsImageEditorOpen(false);
                                        setImageFile(null);
                                        setFormData(prev => ({ ...prev, image_url: "" }));
                                        setCrop(undefined);
                                        setCompletedCrop(undefined);
                                    }}
                                    className="text-gray-500 hover:text-gray-700 text-2xl transition-colors"
                                >
                                    ×
                                </button>
                            </div>

                            <div className="mb-4">
                                <p className="text-sm text-[--text-color] mb-2">
                                    Adjust the crop area to fit the circle. The image will be automatically cropped to a circle.
                                </p>
                                <div className="flex gap-2 mb-4">
                                    <button
                                        onClick={resetCrop}
                                        className="flex items-center gap-1 px-3 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition text-sm"
                                    >
                                        <FaUndo /> Reset Crop
                                    </button>
                                </div>
                            </div>

                            {/* React Image Crop Component */}
                            <div className="flex lg:flex-row gap-3 items-center justify-center">

                                {/* Preview Section */}
                                <div className="flex flex-col items-center lg:items-center lg:w-48 flex-shrink-0">
                                    <h3 className="text-sm font-medium text-black mb-3">Preview:</h3>
                                    {!!completedCrop ? (
                                        <canvas
                                            ref={previewCanvasRef}
                                            className="w-32 h-32 rounded-full border-2 border-gray-300 shadow-md"
                                        />
                                    ) : (
                                        <div className="w-32 h-32 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center">
                                            <span className="text-xs text-gray-500 text-center px-2">
                                                Crop area to see preview
                                            </span>
                                        </div>
                                    )}

                                    {/* Preview Info */}
                                    {!!completedCrop && (
                                        <div className="mt-3 text-xs text-gray-600 text-center lg:text-left">
                                            <p>Final image will be</p>
                                            <p>circular format</p>
                                        </div>
                                    )}
                                </div>

                                {/* Crop Area */}
                                <div className="flex-1">
                                    {!!originalImage && (
                                        <ReactCrop
                                            crop={crop}
                                            onChange={(_, percentCrop) => setCrop(percentCrop)}
                                            onComplete={(c) => setCompletedCrop(c)}
                                            aspect={CIRCLE_ASPECT}
                                            circularCrop
                                            className="max-h-[200px]"
                                        >
                                            <img
                                                ref={imgRef}
                                                alt="Crop me"
                                                src={originalImage}
                                                onLoad={onImageLoad}
                                                className="max-w-full max-h-[400px] object-contain"
                                            />
                                        </ReactCrop>
                                    )}
                                </div>
                            </div>

                            <div className="flex justify-end space-x-3 mt-6">
                                <button
                                    onClick={() => {
                                        setIsImageEditorOpen(false);
                                        setImageFile(null);
                                        setFormData(prev => ({ ...prev, image_url: "" }));
                                        setCrop(undefined);
                                        setCompletedCrop(undefined);
                                    }}
                                    className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={applyCrop}
                                    disabled={!completedCrop}
                                    className="px-6 py-2 bg-[--primary-btn-color] text-white rounded-lg hover:bg-[--secondary-btn-color] transition flex items-center gap-2 font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                                >
                                    <FaCheck /> Apply Crop
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Detail View Modal - remains the same */}
            {isDetailModalOpen && selectedShayar && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex justify-between items-start mb-4">
                                <h2 className="text-2xl font-bold text-[--primary-color]">
                                    {selectedShayar.name || selectedShayar.shayar_name}
                                </h2>
                                <button
                                    onClick={() => setIsDetailModalOpen(false)}
                                    className="text-black hover:text-black text-2xl"
                                >
                                    ×
                                </button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                <div className="md:col-span-1">
                                    <div className="w-32 h-32 mx-auto rounded-full from-[--primary-btn-color] to-[--secondary-btn-color] p-2">
                                        <img
                                            src={selectedShayar.image_url || getDefaultImage(selectedShayar.name || selectedShayar.shayar_name)}
                                            alt={selectedShayar.name || selectedShayar.shayar_name}
                                            className="w-full h-full rounded-full object-cover"
                                        />
                                    </div>
                                </div>

                                <div className="md:col-span-2">
                                    <div className="text-center md:text-left">
                                        <div className="text-lg p-3 text-[--text-color-g]" id='primary-urdu-fonts' dir="rtl">
                                            {selectedShayar.urdu || selectedShayar.shayar_urdu}
                                        </div>
                                        <div className="text-md text-[--text-color-g] mt-1">
                                            {selectedShayar.hindi || selectedShayar.shayar_hindi}
                                        </div>
                                    </div>

                                    <div className="mt-4 space-y-2">
                                        <div className="flex items-center text-sm text-gray-600">
                                            <FaCalendar className="mr-2 text-[--primary-btn-color]" />
                                            <span className="font-medium text-black">Year:</span>
                                            <span className="ml-2">{selectedShayar.year || "Unknown"}</span>
                                        </div>
                                        <div className="flex items-center text-sm text-gray-600">
                                            <FaMapMarkerAlt className="mr-2 text-[--primary-btn-color]" />
                                            <span className="font-medium text-black">Place:</span>
                                            <span className="ml-2">{selectedShayar.place || "Unknown"}</span>
                                        </div>
                                        <div className="flex items-center text-sm text-gray-600">
                                            <span className="font-medium text-black">Status:</span>
                                            <span className={`ml-2 px-2 py-1 text-xs rounded-full ${selectedShayar.is_published === false
                                                ? 'bg-red-100 text-red-800'
                                                : 'bg-green-100 text-green-800'
                                                }`}>
                                                {selectedShayar.is_published === false ? 'Inactive' : 'Active'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {selectedShayar.brief_description && (
                                <div className="mb-4">
                                    <h3 className="text-lg font-semibold text-[--secondary-color] mb-2">Brief Description</h3>
                                    <p className="text-gray-700 leading-relaxed">{selectedShayar.brief_description}</p>
                                </div>
                            )}

                            {selectedShayar.description && (
                                <div className="mb-4">
                                    <h3 className="text-lg font-semibold text-[--secondary-color] mb-2">Description</h3>
                                    <p className="text-gray-700 leading-relaxed whitespace-pre-line">{selectedShayar.description}</p>
                                </div>
                            )}

                            <div className="flex justify-end mt-6">
                                <button
                                    onClick={() => setIsDetailModalOpen(false)}
                                    className="px-6 py-2 bg-[--primary-btn-color] text-white rounded-lg hover:bg-[--secondary-btn-color] transition duration-200"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Shayars;